import { useState } from 'react';
import { View, TouchableOpacity, LayoutAnimation, Platform, UIManager } from 'react-native';
import { Badge } from 'react-native-paper';
import usePlatform from '@/utils/hooks/usePlatform';
import SelectInput from '@/components/General/inputs/SelectInput';
import Input from '@/components/General/inputs/Input';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import { StyleSheet } from 'react-native-unistyles';
import SearchBox from '@/components/General/inputs/SearchBox';
import Unicon from '@/components/General/Unicon';
import { Text } from 'react-native';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
}

const TableFilters = ({ tableName, filters, onFiltersChange, onSearchChange, onClearFilters }) => {
    const { isPortrait } = usePlatform();

    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [searchText, setSearchText] = useState('');

    const toggleFilters = () => {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        setFiltersExpanded(prev => !prev);
    };

    const handleFilterSelect = (columnName, value) => {
        onFiltersChange(filters.map(filter => filter.label === columnName ? {...filter, value} : filter))
    };

    const renderFilterInput = (filter) => {
        switch (filter.inputType) {
            case 'component': 
                return filter.component
            case 'dropdown':
                return (
                    <View style={styles.filterContainer}>
                        <SelectInput
                            title={filter.label}
                            placeholder={filter.label}
                            options={filter.options}
                            value={filter.value || filter.defaultValue}
                            onUpdate={(newValue) => handleFilterSelect(filter.label, newValue)}
                        />
                    </View>
                );
            case 'sheetselector':
                return (
                    <View style={styles.filterContainer}>
                        <SheetSelector
                            titleOnly={!isPortrait}
                            title={filter.label}
                            type={filter.sheetType}
                            multiple={filter.multiple}
                            includeArchived={filter.includeArchived}
                            placeholder={filter.label}
                            value={filter.value || filter.defaultValue}
                            onChange={(newValue) => handleFilterSelect(filter.label, newValue)}
                        />
                    </View>
                );
            case 'numberrange':
                return (
                    <View style={styles.dateRangeContainer}>
                        <View style={styles.dateRangeInput}>
                            <Input
                                title={`Min ${filter.label}`}
                                placeholder="Min"
                                value={filter.value?.min ?? ''}
                                onUpdate={(newValue) => handleFilterSelect(filter.label, {
                                    ...filter.value,
                                    min: newValue
                                })}
                            />
                        </View>
                        <View style={styles.dateRangeInput}>
                            <Input
                                title={`Max ${filter.label}`}
                                placeholder="Max"
                                value={filter.value?.max ?? ''}
                                onUpdate={(newValue) => handleFilterSelect(filter.label, {
                                    ...filter.value,
                                    max: newValue
                                })}
                            />
                        </View>
                    </View>
                );
            case 'text':
                return (
                    <View style={styles.filterContainer}>
                        <Input
                            title={filter.label}
                            placeholder={filter.label}
                            defaultValue={filter.defaultValue}
                            onUpdate={(newValue) => handleFilterSelect(filter.label, newValue)}
                        />
                    </View>
                );
            case 'daterange':
                return (
                    <View style={styles.dateRangeContainer}>
                        <View style={styles.dateRangeInput}>
                            <DatePickerInput
                                title="Start Date"
                                mode="date"
                                value={filter.value?.start}
                                onUpdate={(newValue) => handleFilterSelect(filter.label, { 
                                    ...filter.value, 
                                    start: newValue 
                                })}
                            />
                        </View>
                        <View style={styles.dateRangeInput}>
                            <DatePickerInput
                                title="End Date"
                                mode="date"
                                value={filter.value?.end}
                                minimumDate={filter.value?.start}
                                onUpdate={(newValue) => handleFilterSelect(filter.label, { 
                                    ...filter.value, 
                                    end: newValue 
                                })}
                            />
                        </View>
                    </View>
                );
            default:
                return null;
        }
    };

    const renderFilters = () => {
        return filters?.map((filter, index) => (
            <View key={index}>
                {renderFilterInput(filter)}
            </View>
        ));
    };

    const isFilterActive = (f) => {
        const v = f.value ?? f.defaultValue;
        if (v == null) return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') return Object.values(v).some(x => x !== '' && x != null);
        return true;
    };

    const activeFilterCount = filters?.filter(isFilterActive).length;

    if (isPortrait) {
        return (
            <View>
                <View style={styles.mobileContainer}>
                    <View style={styles.flex1}>
                        <SearchBox isNotOnSurface={false} testID={tableName} placeholder="Search" searchText={searchText} onChangeText={(text) => {
                                setSearchText(text)
                                onSearchChange(text)
                            }}
                        />
                    </View>

                    {filters?.length > 0 &&
                        <TouchableOpacity onPress={toggleFilters} style={styles.mobileFilterButton(filtersExpanded)}>
                            <Unicon name={filtersExpanded ? "close-outline" : "filter-outline"} size={22} colorVariant={filtersExpanded ? "text" : "primary"} />
                            {!filtersExpanded && activeFilterCount > 0 && (
                                <Badge style={styles.badge}>{activeFilterCount}</Badge>
                            )}
                        </TouchableOpacity>
                    }
                </View>

                {filtersExpanded && (
                    <View style={styles.collapsibleContent}>
                        {renderFilters()}
                        {onClearFilters && (
                            <TouchableOpacity onPress={onClearFilters} style={styles.clearButtonMobile}>
                                <Unicon name="filter-circle-xmark" library="fa" size={16} colorVariant="textSecondary" />
                                <Text style={styles.clearButtonMobileText}>Clear filters</Text>
                            </TouchableOpacity>
                        )}
                    </View>
                )}
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {filtersExpanded ? renderFilters() : null}
            
            <View style={styles.buttonRow}>
                {!filtersExpanded && filters?.length > 0 && (
                    <TouchableOpacity onPress={toggleFilters} style={styles.showFiltersButton}>
                        <Unicon name="filter" library="material" size={16} colorVariant="primary" />
                        <Text style={styles.showFiltersText}>Show filters</Text>
                    </TouchableOpacity>
                )}
                {filtersExpanded && filters?.length > 0 && (
                    <TouchableOpacity onPress={toggleFilters} style={styles.hideFiltersButton}>
                        <Unicon name="filter-off" library="material" size={16} colorVariant="primary" />
                        <Text style={styles.hideFiltersText}>Hide Filters</Text>
                    </TouchableOpacity>
                )}
                {(onClearFilters && activeFilterCount > 0) && (
                    <TouchableOpacity onPress={onClearFilters} style={styles.clearButtonDesktop}>
                        <Unicon name="filter-remove" library="material" size={16} colorVariant="error" />
                        <Text style={styles.clearButtonText}>Clear Filters</Text>
                    </TouchableOpacity>
                )}
            </View>
            {(onSearchChange && typeof onSearchChange === "function") &&
                <View style={styles.searchContainer}>
                    <SearchBox isNotOnSurface={false} testID={tableName} searchText={searchText} onChangeText={(text) => {
                        setSearchText(text)
                        onSearchChange(text)
                    }} placeholder="Search"/>
                </View>
            }
        </View>
    );
};

const styles = StyleSheet.create(({colors, fonts}, rt) => ({
    container: {
        gap: 8,
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "flex-end",
    },
    filterContainer: {
        width: rt.isPortrait ? "100%" : 220,
    },
    mobileFilterButton: (isExpanded) => ({
        width: 36,
        height: 36,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isExpanded ? colors.surfaceVariant : 'transparent',
    }),
    badge: {
        position: 'absolute',
        top: -4,
        right: -4,
    },
    collapsibleContent: {
        paddingTop: 8,
        gap: 4,
    },
    mobileContainer: {
        flexDirection: "row", 
        alignItems: "center", 
        gap: 8,
    },
    flex1: {
        flex: 1,
    },
    clearButtonMobile: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingVertical: 8,
    },
    clearButtonMobileText: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 13,
    },
    clearButtonDesktop: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 6,
        backgroundColor: colors.error + '15',
        borderRadius: 6,
        _web:{
            _hover: {
                opacity:0.8
            }
        }
    },
    clearButtonText: {
        color: colors.error,
        fontFamily: fonts.body,
        fontSize: 13,
    },
    buttonRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'flex-end',
        marginBottom: 13
    },
    showFiltersButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 6,
        borderRadius: 6,
        _web: {
            _hover: {
                opacity: 0.8,
            }
        }
    },
    showFiltersText: {
        color: colors.primary,
        fontFamily: fonts.body,
        fontSize: 13,
    },
    hideFiltersButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 6,
        borderRadius: 6,
        _web: {
            _hover: {
                opacity: 0.8,
            }
        }
    },
    hideFiltersText: {
        color: colors.primary,
        fontFamily: fonts.body,
        fontSize: 13,
    },
    searchContainer: {
        width: 300,
        marginLeft: 'auto',
        marginBottom: 8,
    },
    dateRangeContainer: {
        flexDirection: rt.isPortrait ? 'column' : 'row',
        gap: 8,
    },
    dateRangeInput: {
        width: rt.isPortrait ? "100%" : 180,
    }
}));

export default TableFilters;
